<template>
  <ErrorAlert v-if="error != null" :error="error" class="mt-0 mb-6 rounded" @click.stop />
  <v-fade-transition mode="out-in">
    <v-row v-if="isFetching">
      <v-col v-for="n in skeletonLoaderAmount" :key="n">
        <v-skeleton-loader class="rounded-lg" height="150" type="image" />
      </v-col>
    </v-row>
    <v-row v-else>
      <template v-for="(reportData, reportDataIndex) in reportStatisticsDashboardData">
        <v-col
          :key="reportDataIndex"
          cols="12"
          :md="currentUser?.hasRole('Customer') ? 12 : 4"
          v-if="reportData.hasPermission"
        >
          <ReportWidget
            report-type="guard"
            :count="reportData.count"
            :text="reportData.text"
            :color="reportData.color"
            :filters="reportData.filters"
          />
        </v-col>
      </template>
    </v-row>
  </v-fade-transition>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { useDate } from 'vuetify'
import { storeToRefs } from 'pinia'

import type DateFnsUtils from '@date-io/date-fns/build/date-fns-utils'
import { useAuthStore } from '@/stores'

import { ReportStateEnum, GuardReportStatistics } from '@/models/report'

import ErrorAlert from '../common/ErrorAlert.vue'
import ReportWidget from './ReportWidget.vue'

import { useQuery } from '@tanstack/vue-query'
import guardReportService from '@/services/guard_report/guard-report'

const skeletonLoaderAmount = computed(() => (currentUser.value?.hasRole('Customer') ? 1 : 3))
const dateAdapter = useDate() as DateFnsUtils

function useFetchGuardReportStatistics() {
  const queryKey = ref(['guard-reports-statistics'])

  const {
    data: statistics,
    isFetching,
    error
  } = useQuery({
    queryKey: queryKey.value,
    queryFn: () => guardReportService.fetchGuardReportStatistics(),
    select: (data) => new GuardReportStatistics(data)
  })

  return { queryKey, statistics, isFetching, error }
}

const { statistics, isFetching, error } = useFetchGuardReportStatistics()

const authStore = useAuthStore()
const { getUser: currentUser } = storeToRefs(authStore)

const reportStatisticsDashboardData = computed(() => [
  {
    hasPermission:
      currentUser.value?.hasPermission('guard_reports.add_guardreport') &&
      !currentUser.value?.hasPermission('guard_reports.approve_guardreport'),
    color: 'draft',
    count: statistics.value?.draft_count ?? 0,
    text: `of your report${statistics.value?.draft_count != 1 ? 's are' : ' is'} in draft`,
    filters: {
      owner: currentUser.value?.id!,
      state: ReportStateEnum.Draft
    }
  },
  {
    hasPermission: currentUser.value?.hasPermission('guard_reports.approve_guardreport'),
    color: 'draft',
    count: statistics.value?.waiting_submission_count ?? 0,
    text: `report${statistics.value?.waiting_submission_count != 1 ? 's are' : ' is'} in draft`,
    filters: {
      state: ReportStateEnum.Draft
    }
  },
  {
    hasPermission: currentUser.value?.hasPermission('guard_reports.add_guardreport'),
    color: 'submitted',
    count: statistics.value?.approval_pending_count ?? 0,
    text: `report${
      statistics.value?.approval_pending_count != 1 ? 's' : ''
    } you have pending approval`,
    filters: {
      owner: currentUser.value?.id,
      state: ReportStateEnum.Submitted
    }
  },
  {
    hasPermission: currentUser.value?.hasPermission('guard_reports.approve_guardreport'),
    color: 'primary',
    count: statistics.value?.waiting_approval_count ?? 0,
    text: `report${
      statistics.value?.waiting_approval_count != 1 ? 's are' : ' is'
    } awaiting approval`,
    filters: {
      state: ReportStateEnum.Submitted
    }
  },
  {
    hasPermission:
      !currentUser.value?.hasPermission('guard_reports.approve_guardreport') &&
      !currentUser.value?.hasRole('Customer'),
    color: 'approved',
    count: statistics.value?.recently_created_count ?? 0,
    text: `report${
      statistics.value?.recently_created_count != 1 ? 's' : ''
    } created this past week`,
    filters: {
      created_after: dateAdapter.formatByString(dateAdapter.addWeeks(new Date(), -1), 'yyyy-MM-dd'),
      owner: currentUser.value?.id
    }
  },
  {
    hasPermission: currentUser.value?.hasRole('Customer'),
    color: 'approved',
    count: statistics.value?.recently_approved_for_associated_sites_count ?? 0,
    text: `report${
      statistics.value?.recently_approved_for_associated_sites_count != 1 ? 's' : ''
    } approved this past week`,
    filters: {
      modified_after: dateAdapter.formatByString(
        dateAdapter.addWeeks(new Date(), -1),
        'yyyy-MM-dd'
      ),
      state: ReportStateEnum.Approved
    }
  }
])
</script>
